import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Trip, TripStatus } from '../types/trip';
import {
  getAllTrips,
  createTrip,
  updateTrip,
  startTrip,
  finishTrip,
  cancelTrip,
  getTrip,
  uploadRevenueAttachment,
  type TripCreateDto,
  type TripUpdateDto,
  type TripStartDto,
} from '../lib/api/endpoints';
import { tripFromDto, TRIP_STATUS_TO_DTO } from '../lib/api/mappers';
import { comprimirRecibo } from '../utils/receiptImage';

export interface TripsFilter {
  status?: TripStatus;
  vehicleId?: string;
  driverId?: string;
  subunitId?: string;
  /** ⚠️ `from`/`to` filtran por SOLAPE con la ventana, no por contención. */
  from?: string;
  to?: string;
}

/**
 * FE-11 — listado de viajes y las cinco operaciones que lo mueven.
 *
 * El backend ya aplica la visibilidad por rol (`GET /trips`): el Operador ve
 * solo los suyos, el Supervisor su sub-unidad. Este hook NO vuelve a filtrar
 * por tenant ni por rol — hacerlo en el cliente sería fingir una autorización
 * que no tenemos (regla de oro del contrato §7).
 *
 * Las cinco acciones devuelven el `Trip` ya actualizado y lo reemplazan en la
 * lista sin recargarla entera. Lanzan `ApiError` tal cual: el `409` de solape
 * y el `403` de permiso son información de negocio, y el llamador es quien
 * sabe cómo contarlos.
 */
export function useTrips(filter: TripsFilter = {}) {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  // El filtro llega como objeto literal desde el componente: sin esto, cada
  // render dispararía una carga nueva.
  const filterKey = JSON.stringify(filter);

  useEffect(() => {
    let cancelled = false;
    const parsed = JSON.parse(filterKey) as TripsFilter;

    const load = async () => {
      setIsLoading(true);
      try {
        const items = await getAllTrips({
          status: parsed.status ? TRIP_STATUS_TO_DTO[parsed.status] : undefined,
          vehicleId: parsed.vehicleId,
          driverId: parsed.driverId,
          subunitId: parsed.subunitId,
          from: parsed.from,
          to: parsed.to,
        });
        if (cancelled) return;
        setTrips(items.map(tripFromDto));
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setTrips([]);
          setError(err instanceof Error ? err : new Error('Error desconocido cargando viajes'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [filterKey, reloadToken]);

  const reload = useCallback(() => setReloadToken(t => t + 1), []);

  // El listado llega "el más reciente primero" y así se conserva al sustituir
  // un viaje: reemplazar en sitio no cambia el orden que decidió el servidor.
  const upsert = useCallback((trip: Trip) => {
    setTrips(prev => {
      const idx = prev.findIndex(t => t.id === trip.id);
      if (idx === -1) return [trip, ...prev];
      const next = [...prev];
      next[idx] = trip;
      return next;
    });
  }, []);

  const acciones = useMemo(() => ({
    programar: async (data: TripCreateDto): Promise<Trip> => {
      const trip = tripFromDto(await createTrip(data));
      upsert(trip);
      return trip;
    },
    // Corregir mueve la ventana y con ella el expediente entero (ADR-024):
    // quien muestre ruta o alertas de este viaje tiene que volver a pedirlas.
    corregir: async (tripId: string, data: TripUpdateDto): Promise<Trip> => {
      const trip = tripFromDto(await updateTrip(tripId, data));
      upsert(trip);
      return trip;
    },
    // FE-34: con tarifa, `data.quantity` es el peso del manifiesto y el core
    // crea el ingreso en la misma transacción; el `Trip` vuelve con `revenueId`.
    iniciar: async (tripId: string, data: TripStartDto = {}): Promise<Trip> => {
      const trip = tripFromDto(await startTrip(tripId, data));
      upsert(trip);
      return trip;
    },
    /**
     * FE-34 — la foto del manifiesto cuelga del INGRESO del viaje, no del
     * viaje (ADR-032 §2.4), y es lo único de ingresos que el Operador puede
     * tocar (#225). Por eso no pasa por `useRevenueAttachments`: ese hook
     * recarga el ingreso al terminar y al Operador eso le da 403. Aquí se sube
     * y se recarga el VIAJE, que es lo que trae `hasManifest`.
     */
    adjuntarManifiesto: async (trip: Trip, file: File): Promise<Trip> => {
      if (!trip.revenueId) throw new Error('Este viaje no tiene ingreso al que adjuntar el manifiesto.');
      await uploadRevenueAttachment(trip.revenueId, await comprimirRecibo(file));
      const actualizado = tripFromDto(await getTrip(trip.id));
      upsert(actualizado);
      return actualizado;
    },
    finalizar: async (tripId: string, at?: string): Promise<Trip> => {
      const trip = tripFromDto(await finishTrip(tripId, at));
      upsert(trip);
      return trip;
    },
    cancelar: async (tripId: string, reason: string): Promise<Trip> => {
      const trip = tripFromDto(await cancelTrip(tripId, reason));
      upsert(trip);
      return trip;
    },
  }), [upsert]);

  return { trips, isLoading, error, reload, ...acciones };
}
